import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useDataTableContext } from "@msuite/ui/table";
import type { Enums } from "@msuite/utils/types";
import { Fragment } from "react";
import { useCitadel } from "@/hooks/use-citadel";
import { AddAcknowledgementDefinitionModal } from "@/modals/organizations/add-acknowledgement-definition-modal";

/** Props Interface */
interface OrganizationSettingsWorksitesAcknowledgementDefinitionsMenuProps {
	worksite: Enums<"attendanceWorksiteType">;
	projectId?: string;
}

export function OrganizationSettingsWorksitesAcknowledgementDefinitionsMenu({
	worksite,
	projectId,
}: OrganizationSettingsWorksitesAcknowledgementDefinitionsMenuProps) {
	/** Context */
	const { refetch } = useDataTableContext();

	/** Hooks */
	const { t } = useTranslation();
	const disclosure = useDisclosure();
	const { citadel } = useCitadel();

	/** Computed */
	// `POST /organizations/acknowledgement-definitions` is gated on `create Organization`, while
	// the tab this sits on only needs `read Organization` — so a caller who may look at the
	// worksite rules is not thereby allowed to add one. Three states rather than two: a rule list
	// still in flight is not a denial, the same guard `Can` documents.
	const createIsDenied = !!citadel && citadel.cannot("create", "Organization");

	/** Render */
	return (
		<Fragment>
			<Button
				variant="primary"
				onClick={disclosure.onOpen}
				icon={<Icon name="Plus" />}
				isDisabled={createIsDenied}
			>
				{t("general.add")}
			</Button>
			{disclosure.isOpen && (
				<AddAcknowledgementDefinitionModal
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
					worksite={worksite}
					projectId={projectId}
					onComplete={refetch}
				/>
			)}
		</Fragment>
	);
}
